import { useMemo } from 'react';
import { BsCheck2All } from 'react-icons/bs';
import {
  MdCheck,
  MdCheckCircle,
  MdClose,
  MdExpandMore,
  MdUndo,
} from 'react-icons/md';
import { Menu } from '@headlessui/react';
import {
  Button,
  DropdownItem,
  DropdownItems,
  usePopperDropdown,
  useToastNotifications,
} from '@nbirl/nbi-common-ui-libs';
import Portal from '@reach/portal';

import { useErrorToast } from '../../../../../../../shared/hooks';
import { DocumentListRow, DocumentStatus } from '../../../../models';
import { useBulkChangeDocumentsStatusMutation } from '../../../../queries';

type BulkChangeStatusProps = {
  folders: DocumentListRow[];
  documents: DocumentListRow[];
  onStatusChangeSuccess: () => void;
};

export const BulkChangeStatus = ({
  folders,
  documents,
  onStatusChangeSuccess,
}: BulkChangeStatusProps) => {
  const { referenceElementProps, popperElementProps } = usePopperDropdown({
    placement: 'bottom-start',
  });
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const bulkChangeDocumentsStatusMutation =
    useBulkChangeDocumentsStatusMutation();

  const [pendingDocuments, otherDocuments] = useMemo(() => {
    if (documents.length === 0) {
      return [[], []];
    }

    return [
      documents.filter(({ status }) => status === 'PENDING'),
      documents.filter(({ status }) => status !== 'PENDING'),
    ];
  }, [documents]);

  const [canBulkApproveOrReject, canBulkPending] = useMemo(() => {
    if (folders.length > 0) {
      return [false, false];
    }

    return [
      pendingDocuments.length > 0 && otherDocuments.length === 0,
      otherDocuments.length > 0 && pendingDocuments.length === 0,
    ];
  }, [folders.length, pendingDocuments.length, otherDocuments.length]);

  const getSuccessMessage = (count: number, status: DocumentStatus) => {
    const messagePrefix = `${count} ${
      count > 1 ? 'documents' : 'document'
    } successfully changed to`;

    switch (status) {
      case 'PUBLISHED':
        return `${messagePrefix} approved.`;
      case 'REJECTED':
        return `${messagePrefix} rejected.`;
      default:
        return `${messagePrefix} pending approval.`;
    }
  };

  const handleStatusChange = (status: DocumentStatus) => {
    const documentIds = documents.map(({ id }) => id);

    bulkChangeDocumentsStatusMutation.mutate(
      { status, documentIds },
      {
        onSuccess: () => {
          addToast({
            variant: 'success',
            text: getSuccessMessage(documentIds.length, status),
            icon: MdCheckCircle,
          });
          onStatusChangeSuccess();
        },
        onError: () => {
          showErrorToast('Something went wrong. Please try again.');
        },
      }
    );
  };

  return (
    <Menu>
      <Menu.Button
        as={Button}
        variant="ghost"
        leftIcon={<BsCheck2All />}
        rightIcon={<MdExpandMore />}
        disabled={!canBulkApproveOrReject && !canBulkPending}
        {...referenceElementProps}
      >
        Change Status
      </Menu.Button>
      <Portal>
        <DropdownItems {...popperElementProps}>
          {canBulkApproveOrReject && (
            <>
              <DropdownItem
                icon={<MdCheck className="text-gray-300" />}
                onClick={() => handleStatusChange('PUBLISHED')}
              >
                Approved
              </DropdownItem>
              <DropdownItem
                icon={<MdClose className="text-gray-300" />}
                onClick={() => handleStatusChange('REJECTED')}
              >
                Rejected
              </DropdownItem>
            </>
          )}

          {canBulkPending && (
            <DropdownItem
              icon={<MdUndo className="text-gray-300" />}
              onClick={() => handleStatusChange('PENDING')}
            >
              Pending Approval
            </DropdownItem>
          )}
        </DropdownItems>
      </Portal>
    </Menu>
  );
};
